<template>
  <section class="page-section">
    <div class="toolbar filter-wrap">
      <el-select v-model="filters.applyStatus" placeholder="审批状态" clearable class="toolbar-select">
        <el-option label="待审批" :value="0" />
        <el-option label="审批通过" :value="1" />
        <el-option label="审批拒绝" :value="2" />
      </el-select>
      <el-button type="primary" @click="handleSearch">搜索</el-button>
      <el-button @click="handleReset">重置</el-button>
    </div>

    <el-alert
      title="审批通过后将自动扣减库存。"
      type="info"
      :closable="false"
      show-icon
      class="section-alert"
    />

    <el-table :data="requests.content" class="business-table">
      <el-table-column prop="userUsername" label="员工" width="120" />
      <el-table-column prop="suppliesName" label="用品" min-width="140" />
      <el-table-column prop="quantity" label="数量" width="80" />
      <el-table-column prop="reason" label="用途说明" min-width="180" show-overflow-tooltip />
      <el-table-column prop="createTime" label="提交时间" width="180" />
      <el-table-column label="审批状态" width="100">
        <template #default="{ row }">
          <el-tag :type="requestTagType(row.applyStatus)">
            {{ requestStatusLabel(row.applyStatus) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="rejectReason" label="审批信息" min-width="180" show-overflow-tooltip />
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="{ row }">
          <template v-if="row.applyStatus === 0">
            <el-button link type="primary" @click="handleReview(row)">通过</el-button>
            <el-button link type="danger" @click="openRejectDialog(row)">驳回</el-button>
          </template>
          <span v-else class="muted-text">已处理</span>
        </template>
      </el-table-column>
    </el-table>

    <div v-if="requests.totalElements > 0" class="pager">
      <span class="pager-text">第 {{ filters.page }} / {{ totalPages }} 页</span>
      <el-button :disabled="filters.page <= 1" @click="handlePrevPage">
        <el-icon><ArrowLeft /></el-icon>
        上一页
      </el-button>
      <el-button type="primary" :disabled="filters.page >= totalPages" @click="handleNextPage">
        下一页
        <el-icon><ArrowRight /></el-icon>
      </el-button>
    </div>

    <el-dialog v-model="rejectDialogVisible" title="填写审批信息" width="460px">
      <el-form label-position="top">
        <el-form-item label="审批信息" required>
          <el-input v-model="rejectReason" type="textarea" :rows="4" maxlength="120" show-word-limit />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="rejectDialogVisible = false">取消</el-button>
        <el-button type="danger" @click="submitReject">确认驳回</el-button>
      </template>
    </el-dialog>
  </section>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import { ArrowLeft, ArrowRight } from '@element-plus/icons-vue'
import {
  approveAdminRequest,
  fetchAdminRequests,
  rejectAdminRequest
} from '../../services/adminService'

export default {
  name: 'AdminRequestsPanel',
  components: {
    ArrowLeft,
    ArrowRight
  },
  emits: ['notifications-changed'],
  data() {
    return {
      requests: {
        content: [],
        totalElements: 0
      },
      filters: {
        applyStatus: '',
        page: 1,
        pageSize: 10
      },
      rejectDialogVisible: false,
      rejectReason: '',
      currentRequest: null
    }
  },
  computed: {
    totalPages() {
      const total = Number(this.requests.totalElements) || 0
      const pageSize = Number(this.filters.pageSize) || 10

      return Math.max(1, Math.ceil(total / pageSize))
    }
  },
  mounted() {
    this.loadRequests()
  },
  methods: {
    requestTagType(status) {
      if (status === 0) return 'warning'
      if (status === 1) return 'success'
      if (status === 2) return 'danger'
      return 'info'
    },
    requestStatusLabel(status) {
      if (status === 0) return '待审批'
      if (status === 1) return '审批通过'
      if (status === 2) return '审批拒绝'
      return '已取消'
    },
    async loadRequests() {
      const response = await fetchAdminRequests(this.filters)
      this.requests = response.data
    },
    handlePageChange(page) {
      this.filters.page = page
      this.loadRequests()
    },
    handlePrevPage() {
      if (this.filters.page <= 1) {
        return
      }

      this.handlePageChange(this.filters.page - 1)
    },
    handleNextPage() {
      if (this.filters.page >= this.totalPages) {
        return
      }

      this.handlePageChange(this.filters.page + 1)
    },
    handleSearch() {
      this.filters.page = 1
      this.loadRequests()
    },
    handleReset() {
      this.filters.applyStatus = ''
      this.filters.page = 1
      this.loadRequests()
    },
    isCancelAction(error) {
      return error === 'cancel' || error === 'close'
    },
    async handleReview(row) {
      try {
        await ElMessageBox.confirm(`确认通过员工「${row.userUsername}」的申领单吗？`, '审批确认', {
          type: 'warning',
          confirmButtonText: '确认通过',
          cancelButtonText: '取消'
        })
        const response = await approveAdminRequest(row.id)
        ElMessage.success(response.message || response.msg || '审批完成')
        this.loadRequests()
        this.$emit('notifications-changed')
      } catch (error) {
        if (this.isCancelAction(error)) {
          return
        }

        ElMessage.error(error.message)
      }
    },
    openRejectDialog(row) {
      this.currentRequest = row
      this.rejectReason = ''
      this.rejectDialogVisible = true
    },
    async submitReject() {
      const rejectReason = this.rejectReason.trim()

      if (!rejectReason) {
        ElMessage.warning('请填写审批信息')
        return
      }

      try {
        const response = await rejectAdminRequest(this.currentRequest.id, rejectReason)
        ElMessage.success(response.message || response.msg || '审批完成')
        this.rejectDialogVisible = false
        this.loadRequests()
        this.$emit('notifications-changed')
      } catch (error) {
        ElMessage.error(error.message)
      }
    }
  }
}
</script>

<style scoped>
.page-section {
  display: grid;
  gap: 22px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.filter-wrap {
  justify-content: flex-start;
}

.toolbar-select {
  width: 180px;
}

.business-table {
  border-radius: 22px;
  overflow: hidden;
}

.pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.pager-text {
  color: #475569;
  font-size: 14px;
  font-weight: 600;
}

.section-alert {
  border-radius: 16px;
}

.muted-text {
  color: #94a3b8;
}

@media (max-width: 768px) {
  .toolbar-select {
    width: 100%;
  }
}
</style>
